/**
 * ServiceContainer - 전역 서비스 컨테이너
 * PHP의 app()->getInstance()->bind()와 유사한 기능 제공
 */
export class ServiceContainer {
    static instance = null;

    constructor() {
        this.container = new Map(); // key -> value
    }

    /**
     * 싱글톤 인스턴스 가져오기
     */
    static getInstance() {
        if (!ServiceContainer.instance) {
            ServiceContainer.instance = new ServiceContainer();
        }
        return ServiceContainer.instance;
    }

    /**
     * 값을 공유(싱글톤)로 저장
     * @param {string} key - 저장할 키
     * @param {*} value - 저장할 값
     */
    share(key, value) {
        this.container.set(key, value);
    }

    /**
     * 팩토리 함수를 바인딩 (lazy 생성)
     * @param {string} key - 저장할 키
     * @param {Function} factory - 인스턴스를 생성하는 팩토리 함수
     * @param {boolean} singleton - 싱글톤 여부 (true면 한 번만 생성)
     */
    bind(key, factory, singleton = false) {
        if (singleton) {
            // 싱글톤인 경우 즉시 생성하여 저장
            const instance = factory(this);
            this.container.set(key, instance);
        } else {
            // 팩토리 함수로 저장 (나중에 make()로 호출)
            this.container.set(key, factory);
        }
    }

    /**
     * 바인딩된 팩토리를 호출하여 인스턴스 생성/반환
     * @param {string} key - 조회할 키
     * @param {...*} args - 팩토리 함수에 전달할 인자
     * @returns {*} 저장된 값 또는 팩토리 호출 결과
     */
    make(key, ...args) {
        const entry = this.container.get(key);

        if (entry === undefined) {
            throw new Error(`Service not found: ${key}`);
        }

        if (typeof entry === "function") {
            // 팩토리 함수인 경우 호출
            return entry(this, ...args);
        }

        // 이미 인스턴스인 경우 그대로 반환
        return entry;
    }

    /**
     * 저장된 값을 조회
     * @param {string} key - 조회할 키
     * @param {*} defaultValue - 기본값
     * @returns {*} 저장된 값 또는 기본값
     */
    get(key, defaultValue = null) {
        return this.container.get(key) ?? defaultValue;
    }

    /**
     * 키가 존재하는지 확인
     * @param {string} key - 확인할 키
     * @returns {boolean}
     */
    has(key) {
        return this.container.has(key);
    }

    /**
     * 바인딩되어 있는지 확인 (팩토리 함수인지 체크)
     * @param {string} key - 확인할 키
     * @returns {boolean}
     */
    bound(key) {
        const entry = this.container.get(key);
        return entry !== undefined && typeof entry === "function";
    }

    /**
     * 항목 제거
     * @param {string} key - 제거할 키
     */
    forget(key) {
        this.container.delete(key);
    }
}

// 싱글톤 인스턴스
export const serviceContainer = ServiceContainer.getInstance();

// 전역으로 export
window.ServiceContainer = ServiceContainer;
window.serviceContainer = serviceContainer;
